import { formatCalendarMonth } from '@cascade/core/utils';
import { useI18n } from '@cascade/react/i18n';
import {
  useConversationList,
  useConversations,
} from '@cascade/react/conversations';
import { FeedbackText, Link, Loading, Text } from '@cascade/ui-library';
import { Fragment, useMemo } from 'react';
import { groupConversationsByMonth } from './ConversationList.helpers.ts';
import classes from './ConversationList.module.scss';
import { getConversationPath } from '#config/index.ts';

export const ConversationList = () => {
  const { translate, translateError, locale } = useI18n();
  const { currentConversationId } = useConversations();
  const { conversations, isLoadingConversations, conversationsError } =
    useConversationList({
      excludeEscalated: true,
      excludeGuidedExperience: true,
    });

  const groups = useMemo(
    () => groupConversationsByMonth(conversations),
    [conversations],
  );

  if (isLoadingConversations) {
    return <Loading contain />;
  }

  if (!conversationsError && groups.length === 0) {
    return null;
  }

  return (
    <section
      className={classes['conversation-list']}
      aria-label={translate('navigation.conversations')}
    >
      <Text
        className={classes.heading}
        type='caption'
        color='text-60'
        weight='medium'
        content={translate('navigation.conversations')}
      />
      {conversationsError ? (
        <FeedbackText
          className={classes.feedback}
          feedbackLevel='danger'
          feedbackText={translateError(conversationsError)}
        />
      ) : (
        <div className={classes.groups}>
          {groups.map((group) => (
            <Fragment key={group.month.getTime()}>
              <Text
                className={classes.month}
                type='caption'
                color='text-60'
                content={formatCalendarMonth(group.month, { locale })}
              />
              <ul className={classes.links}>
                {group.conversations.map((conversation) => {
                  const isActive =
                    conversation.conversation_id === currentConversationId;

                  return (
                    <li
                      key={conversation.conversation_id}
                      className={classes.row}
                    >
                      <Link
                        type='internal'
                        variant='secondary'
                        active={isActive}
                        uri={getConversationPath(conversation.conversation_id)}
                        label={conversation.title}
                        title={conversation.title}
                        textProps={{ type: 'body-2', truncated: true }}
                      />
                    </li>
                  );
                })}
              </ul>
            </Fragment>
          ))}
        </div>
      )}
    </section>
  );
};
